import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Users, Mail, Lock, Loader2, AlertCircle, Building2 } from "lucide-react";
import { loginStaff } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

// panelType prop allows fixing to a specific panel (e.g., from admin app)
// availablePanels prop allows restricting which panels are shown
export default function StaffLogin({ panelType: fixedPanelType, availablePanels }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [panelType, setPanelType] = useState(fixedPanelType || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If a fixed panel type is provided, use it
  useEffect(() => {
    if (fixedPanelType) {
      setPanelType(fixedPanelType);
    }
  }, [fixedPanelType]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    const effectivePanelType = fixedPanelType || panelType;
    
    if (!email || !password || !effectivePanelType) {
      setError(t("pleaseFillInAllFields"));
      return;
    }
    
    setLoading(true);
    
    try {
      await loginStaff(email, password, effectivePanelType);
      
      // Navigate based on panel type
      const dashboardMap = {
        ADMIN: "/AdminDashboard",
        ENGINE: "/EngineDashboard",
        RESELLER: "/ResellerDashboard",
        MERCHANT: "/MerchantDashboard",
      };
      
      navigate(dashboardMap[effectivePanelType] || "/AdminDashboard");
    } catch (err) {
      setError(err.message || t("loginFailedCheckCredentials"));
      setLoading(false);
    }
  };

  const allPanelOptions = [
    { value: "ADMIN", label: t("adminPanel"), description: t("administrativeStaff") },
    { value: "ENGINE", label: t("enginePanel"), description: t("engineStaff") },
    { value: "RESELLER", label: t("resellerPanel"), description: t("resellerStaff") },
    { value: "MERCHANT", label: t("merchantPortal"), description: t("merchantStaff") },
  ];

  // Filter panel options if availablePanels is provided
  const panelOptions = availablePanels 
    ? allPanelOptions.filter(opt => availablePanels.includes(opt.value))
    : allPanelOptions;

  // Determine which links to show based on context
  const showAdminLink = !fixedPanelType || fixedPanelType === 'ADMIN';
  const showPortalLinks = !fixedPanelType || ['ENGINE', 'RESELLER', 'MERCHANT'].includes(fixedPanelType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
      <style>{`
        .login-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .glow-effect {
          box-shadow: 0 0 60px rgba(139, 92, 246, 0.3);
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/30 mb-4 glow-effect"
          >
            <Users className="h-10 w-10 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">Demo Gateway</h1>
          <p className="text-purple-200">
            {fixedPanelType 
              ? `${allPanelOptions.find(o => o.value === fixedPanelType)?.label || t("staff")} ${t("staffMemberLogin")}`
              : t("staffMemberLogin")
            }
          </p>
        </div>

        {/* Login Card */}
        <Card className="login-card border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-white">{t("staffAccess")}</CardTitle>
            <CardDescription className="text-purple-200">
              {t("signInWithStaffCredentials")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              {/* Only show panel selector if not fixed to a specific panel */}
              {!fixedPanelType && (
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("panelType")}</Label>
                  <Select value={panelType} onValueChange={setPanelType}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white focus:border-purple-500 focus:ring-purple-500/20">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        <SelectValue placeholder={t("selectYourPanelType")} />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {panelOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-white focus:bg-slate-700 focus:text-white"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{option.label}</span>
                            <span className="text-xs text-slate-400">{option.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    {t("selectPanelYouWereAssignedTo")}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">{t("emailAddress")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="staff@example.com"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t("password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-medium py-5 rounded-xl transition-all duration-200 shadow-lg shadow-purple-500/25"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("signInAsStaff")
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700/50 text-center space-y-2">
              <p className="text-slate-500 text-sm">
                {t("staffAccessOnly")}
              </p>
              <div className="flex gap-2 justify-center text-xs flex-wrap">
                {showAdminLink && (
                  <>
                    <a href="/login" className="text-blue-400 hover:text-blue-300 transition-colors">
                      {t("adminLogin")}
                    </a>
                    {showPortalLinks && <span className="text-slate-600">•</span>}
                  </>
                )}
                {showPortalLinks && !fixedPanelType && (
                  <>
                    <a href="/EngineLogin" className="text-blue-400 hover:text-blue-300 transition-colors">
                      {t("engineLogin")}
                    </a>
                    <span className="text-slate-600">•</span>
                    <a href="/ResellerLogin" className="text-blue-400 hover:text-blue-300 transition-colors">
                      {t("resellerLogin")}
                    </a>
                    <span className="text-slate-600">•</span>
                    <a href="/MerchantLogin" className="text-blue-400 hover:text-blue-300 transition-colors">
                      {t("merchantLogin")}
                    </a>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-6 text-slate-400 text-sm"
        >
          <p>© {new Date().getFullYear()} Demo Gateway. {t("allRightsReserved")}</p>
        </motion.div>
      </motion.div>
    </div>
  );
}
